import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { UserService, UserFilters, UserStatistics } from '../../services/user.service';
import { User } from '../../models/user.model';
import { AuthService } from '../../services/auth.service';

@Component({
  selector: 'app-usuarios',
  templateUrl: './usuarios.component.html',
  styleUrls: ['./usuarios.component.css']
})
export class UsuariosComponent implements OnInit {
  users: User[] = [];
  filteredUsers: User[] = [];
  loading = false;
  error: string | null = null;
  
  // Statistics
  statistics: UserStatistics | null = null;
  
  // Filters
  filters: UserFilters = {
    page: 1,
    limit: 50,
    activo: true // Show only active users by default
  };
  
  searchTerm = '';
  selectedRole: string = '';
  showInactive = false;
  
  // Pagination
  currentPage = 1;
  totalPages = 1;
  totalUsers = 0;
  
  // Role options
  roles = [
    { value: '', label: 'Todos los roles' },
    { value: 'admin', label: 'Administrador' },
    { value: 'mesa', label: 'Mesa de examen' },
    { value: 'recepcion', label: 'Recepción' },
    { value: 'instructor', label: 'Instructor' },
    { value: 'readonly', label: 'Solo lectura' }
  ];

  constructor(
    private userService: UserService,
    private authService: AuthService,
    private router: Router
  ) { }

  ngOnInit(): void {
    this.loadUsers();
    this.loadStatistics();
  }

  /**
   * Load users with current filters
   */
  loadUsers(): void {
    this.loading = true;
    this.error = null;
    
    this.userService.getUsers(this.filters).subscribe(
      response => {
        this.users = response.users;
        this.filteredUsers = response.users;
        this.currentPage = response.pagination.page;
        this.totalPages = response.pagination.totalPages;
        this.totalUsers = response.pagination.total;
        this.loading = false;
      },
      error => {
        console.error('Error loading users:', error);
        this.error = 'Error al cargar los usuarios. Por favor, intente nuevamente.';
        this.loading = false;
      }
    );
  }

  /**
   * Load statistics
   */
  loadStatistics(): void {
    this.userService.getStatistics().subscribe(
      stats => {
        this.statistics = stats;
      },
      error => {
        console.error('Error loading statistics:', error);
      }
    );
  }

  /**
   * Apply filters
   */
  applyFilters(): void {
    this.filters = {
      page: 1,
      limit: 50,
      search: this.searchTerm || undefined,
      role: this.selectedRole || undefined,
      activo: this.showInactive ? undefined : true
    };
    
    this.loadUsers();
  }

  /**
   * Clear filters
   */
  clearFilters(): void {
    this.searchTerm = '';
    this.selectedRole = '';
    this.showInactive = false;
    this.filters = {
      page: 1,
      limit: 50,
      activo: true
    };
    this.loadUsers();
  }

  /**
   * Go to page
   */
  goToPage(page: number): void {
    if (page >= 1 && page <= this.totalPages) {
      this.filters.page = page;
      this.loadUsers();
    }
  }

  /**
   * Navigate to create user
   */
  createNewUser(): void {
    this.router.navigate(['/usuarios/nuevo']);
  }

  /**
   * Navigate to edit user
   */
  editUser(user: User): void {
    this.router.navigate(['/usuarios/editar', user.id]);
  }

  /**
   * Reset user password (with auto-generate option)
   */
  resetUserPassword(user: User): void {
    const generateSecurePassword = (): string => {
      const length = 12;
      const lowercase = 'abcdefghijklmnopqrstuvwxyz';
      const uppercase = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
      const numbers = '0123456789';
      const special = '!@#$%^&*()_+-=[]{}|;:,.<>?';
      
      const allChars = lowercase + uppercase + numbers + special;
      
      let password = '';
      
      // Ensure at least one of each required character type
      password += lowercase[Math.floor(Math.random() * lowercase.length)];
      password += uppercase[Math.floor(Math.random() * uppercase.length)];
      password += numbers[Math.floor(Math.random() * numbers.length)];
      password += special[Math.floor(Math.random() * special.length)];
      
      // Fill the rest randomly
      for (let i = password.length; i < length; i++) {
        password += allChars[Math.floor(Math.random() * allChars.length)];
      }
      
      // Shuffle the password
      return password.split('').sort(() => Math.random() - 0.5).join('');
    };

    const autoPassword = generateSecurePassword();
    
    // Ask if they want to use auto-generated password
    const useAuto = confirm(
      `Resetear contraseña para "${user.username}"?\n\n` +
      `OPCIÓN 1: Usar contraseña autogenerada:\n${autoPassword}\n\n` +
      `Presione OK para usar la contraseña autogenerada.\n` +
      `Presione CANCELAR para ingresar una contraseña manualmente.`
    );

    let newPassword: string | null;

    if (useAuto) {
      newPassword = autoPassword;
      
      // Copy to clipboard
      navigator.clipboard.writeText(newPassword).then(() => {
        alert(`Contraseña autogenerada copiada al portapapeles:\n\n${newPassword}\n\n¡Guárdela en un lugar seguro!`);
      }).catch(() => {
        alert(`Contraseña autogenerada:\n\n${newPassword}\n\n¡Guárdela en un lugar seguro!`);
      });
    } else {
      newPassword = prompt(
        `Ingrese la nueva contraseña para "${user.username}":\n\n` +
        `Requisitos:\n` +
        `- Mínimo 8 caracteres\n` +
        `- Al menos una mayúscula\n` +
        `- Al menos una minúscula\n` +
        `- Al menos un número`
      );

      if (!newPassword) {
        return; // User cancelled
      }

      // Validate password
      if (newPassword.length < 8) {
        alert('La contraseña debe tener al menos 8 caracteres');
        return;
      }

      if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(newPassword)) {
        alert('La contraseña debe contener mayúsculas, minúsculas y números');
        return;
      }
    }

    // Reset password
    this.userService.resetPassword(user.id, newPassword).subscribe(
      response => {
        alert(response.message || 'Contraseña reseteada correctamente. El usuario debe iniciar sesión nuevamente.');
      },
      error => {
        console.error('Error resetting password:', error);
        alert(error.error?.error || 'Error al resetear la contraseña');
      }
    );
  }

  /**
   * Delete user
   */
  deleteUser(user: User): void {
    if (!confirm(`¿Está seguro que desea desactivar al usuario "${user.username}"?`)) {
      return;
    }

    this.userService.deleteUser(user.id).subscribe(
      () => {
        alert('Usuario desactivado correctamente');
        this.loadUsers();
        this.loadStatistics();
      },
      error => {
        console.error('Error deleting user:', error);
        alert(error.error?.error || 'Error al desactivar el usuario');
      }
    );
  }

  /**
   * Reactivate user
   */
  reactivateUser(user: User): void {
    if (!confirm(`¿Está seguro que desea reactivar al usuario "${user.username}"?`)) {
      return;
    }

    this.userService.reactivateUser(user.id).subscribe(
      () => {
        alert('Usuario reactivado correctamente');
        this.loadUsers();
        this.loadStatistics();
      },
      error => {
        console.error('Error reactivating user:', error);
        alert(error.error?.error || 'Error al reactivar el usuario');
      }
    );
  }

  /**
   * Get role display name
   */
  getRoleDisplay(role: string): string {
    const roleMap: { [key: string]: string } = {
      'admin': 'Administrador',
      'mesa': 'Mesa de examen',
      'recepcion': 'Recepción',
      'instructor': 'Instructor',
      'readonly': 'Solo lectura'
    };
    return roleMap[role] || role;
  }

  /**
   * Get role badge class
   */
  getRoleBadgeClass(role: string): string {
    const classMap: { [key: string]: string } = {
      'admin': 'badge-danger',
      'mesa': 'badge-primary',
      'recepcion': 'badge-info',
      'instructor': 'badge-warning',
      'readonly': 'badge-secondary'
    };
    return classMap[role] || 'badge-secondary';
  }

  /**
   * Format date
   */
  formatDate(date: Date | string | undefined): string {
    if (!date) return 'Nunca';
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    return dateObj.toLocaleDateString('es-AR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  /**
   * Format creation date (no time)
   */
  formatCreatedDate(date: Date | string | undefined): string {
    if (!date) return 'N/A';
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    return dateObj.toLocaleDateString('es-AR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
  }
}